import { useEffect, useState } from 'react';
import { useDispatch, useStore } from '../modules/store';
import { useEvent } from '@vfmfe/foundations';
import { BASE_URL_AUTHZ, getLoginProviders, postToken } from '../api';
import { getMarketSegment } from '../utils';
import { getUserInfoAndContextualize } from '../utils/authentication';
import { useLoading } from '../providers/Loading/LoadingProvider';
import { dispatchNotification } from './useNotifications';
import { clearAuthentication } from '../redux/reducers/authorization';
import { closeInteraction } from '../redux/reducers/interactions';
import { useError } from '../providers/Error/ErrorProvider';

export default function useAuthentication() {
    const dispatch = useDispatch();
    const { sendEvent: sendPortalRedirectProceedEvent } = useEvent({ eventName: 'portal-redirect-proceed' });
    const store = useStore();
    const [externalLoginLoadingId, setExternalLoginLoadingId] = useState<string | null>(null);
    const { startLoading, stopLoading } = useLoading();
    const { setError, closeError } = useError();

    //Receive auth token from the post-login window and set the current logged user in the store
    useEffect(() => {
        const handleAuthToken = async (event: MessageEvent) => {
            if (event.origin === window.location.origin) {
                if (event.data.type === 'AUTH_TOKEN') {
                    const loadingId = startLoading('A autenticar...');
                    externalLoginLoadingId && stopLoading(externalLoginLoadingId);
                    //Store the current anon tokens to restore in case of any error during the token exchange
                    const currentAnonAccessToken = localStorage.getItem('accessToken');
                    const currentAnonRefreshToken = localStorage.getItem('refreshToken');
                    try {
                        //Exchange the code for a token and get the user info using the token
                        const postTokenRes = await postToken(event.data.code, event.data.state);
                        if (!postTokenRes.data.access_token || !postTokenRes.data.refresh_token) {
                            throw new Error('Token response does not contain access_token or refresh_token');
                        }
                        localStorage.setItem('accessToken', postTokenRes.data.access_token);
                        localStorage.setItem('refreshToken', postTokenRes.data.refresh_token);
                        await getUserInfoAndContextualize(store);

                        sendPortalRedirectProceedEvent();
                    } catch (error) {
                        console.error('Error handling auth token:', error);
                        //Continue with the previous anon token
                        localStorage.setItem('accessToken', currentAnonAccessToken || '');
                        localStorage.setItem('refreshToken', currentAnonRefreshToken || '');
                    } finally {
                        stopLoading(loadingId);
                    }
                } else if (event.data.type === 'AUTH_ERROR') {
                    externalLoginLoadingId && stopLoading(externalLoginLoadingId);
                    dispatchNotification({
                        message: 'Autenticação falhou. Por favor, tente novamente.',
                        severity: 'error'
                    });
                }
            }
        };

        window.addEventListener('message', handleAuthToken);

        return () => {
            window.removeEventListener('message', handleAuthToken);
        };
    }, [externalLoginLoadingId]);

    useEffect(() => {
        const checkExistingToken = async () => {
            const existingToken = localStorage.getItem('accessToken');
            if (existingToken) {
                let withExistingTokenLoadingId;
                try {
                    withExistingTokenLoadingId = startLoading('A recuperar a sessão (usar token existente)...');
                    await getUserInfoAndContextualize(store);
                } catch (error: any) {
                    console.error('Error fetching user info with existing token:', error);
                    if (error?.response?.status !== 401) {
                        await anonymousAuthentication();
                    }
                } finally {
                    withExistingTokenLoadingId && stopLoading(withExistingTokenLoadingId);
                }
            } else {
                const loadingId = startLoading('A carregar (criando token anónimo)...');
                await anonymousAuthentication();
                stopLoading(loadingId);
            }
        };

        checkExistingToken();
    }, []);

    const anonymousAuthentication = async () => {
        try {
            const postTokenRes = await postToken();
            if (!postTokenRes.data.access_token || !postTokenRes.data.refresh_token) {
                throw new Error('Token response does not contain access_token or refresh_token');
            }
            localStorage.setItem('accessToken', postTokenRes.data.access_token);
            localStorage.setItem('refreshToken', postTokenRes.data.refresh_token);
            dispatch(clearAuthentication());
            dispatch(closeInteraction());
        } catch (error) {
            //Error during anonymous authentication - block ui
            console.error('Error during anonymous authentication:', error);
            setError({
                title: 'Erro de autenticação',
                description: 'Ocorreu um erro durante a autenticação. Por favor, tente recarregar a página.',
                primaryAction: {
                    label: 'Tentar novamente',
                    onClick: () => {
                        closeError();
                        anonymousAuthentication();
                    }
                }
            });
        }
    };

    const handleLoginClick = async () => {
        //Get providers and filter for the current market segment
        const loadingId = startLoading('A obter informações de autenticação...');
        const loginProviders = await getLoginProviders();
        const providerToUse = loginProviders.data.find(
            (provider) => provider.displayName === getMarketSegment().toLowerCase()
        );
        stopLoading(loadingId);
        const externalLoginLoadingId = startLoading('A aguardar login externo...');
        setExternalLoginLoadingId(externalLoginLoadingId);
        const loginWindow = window.open(`${BASE_URL_AUTHZ}${providerToUse?.url}`, '_blank', 'width=800,height=600');

        const timer = setInterval(() => {
            if (loginWindow?.closed) {
                clearInterval(timer);
                stopLoading(externalLoginLoadingId);
                setExternalLoginLoadingId(null);
            }
        }, 1000); // Check every 1000ms
    };

    const handleLogoutClick = async () => {
        const loadingId = startLoading('A terminar sessão...');
        await anonymousAuthentication();
        stopLoading(loadingId);
    };

    //Listen for the login event from mfe's
    useEvent({
        eventName: 'auth-primary-action',
        onReceiveEvent: handleLoginClick
    });

    useEvent({
        eventName: 'auth-secondary-action',
        onReceiveEvent: () => {
            sendPortalRedirectProceedEvent();
        }
    });
    useEvent({
        eventName: 'error-refreshing-token',
        onReceiveEvent: () => {
            dispatchNotification({
                message: 'A sua sessão expirou. Por favor, faça login novamente.',
                severity: 'error'
            });
            handleLogoutClick();
        }
    });

    return {
        handleLoginClick,
        handleLogoutClick
    };
}
